import { Column, Row } from "@/components/container/Container";
import AddressInput from "./AddressInput";
import { Button } from "primereact/button";
import { MdArrowBack, MdClose, MdMap } from "react-icons/md";
import { TextHeadlineSmall, TextLabelLarge2 } from "@/components/text/Texts";
import { cn } from "@/components/ui/cn";
import { Dialog } from "primereact/dialog";
import { showError, showNotification } from "@/modules/notification/engine/notification.library";
import { axiosInstance } from "@/services/axios.service";
import { BaseResponse, createTypedModal, GenericResponse, useTypedModal } from "@/common/common_utils";
import { ENDPOINTS } from "@/constants/endpoints";
import { X4bPointType } from "./AddressDropdownInput";
import { X4bLocation } from "../views/PlacesRepository";
import { useForm } from "react-hook-form";
import * as yup from "yup";
import { yupResolver } from "@hookform/resolvers/yup";
import { VALIDATION_MESSAGES } from "@/constants/validation";
import { useMutation } from "@tanstack/react-query";
import PickAddressMapModal from "../map/pick-address/PickAddressMapModal";
import { useState } from "react";
import TextInput from "@/components/inputs/TextInput";
import { InputController } from "@/components/inputs/InputController";

interface SaveCompanyPointResponse { Id: number }
interface SaveCompanyPointRequest extends X4bLocation {
	PointType: X4bPointType;
}

interface AddNewPlaceModalProps { pointType: X4bPointType }
interface AddNewPlaceForm {
	name: string;
	address: X4bLocation;
}

const AddNewPlaceModal = createTypedModal<AddNewPlaceModalProps, undefined>(({ pointType }: AddNewPlaceModalProps) => {
	const [isMapOpen, setIsMapOpen] = useState(false);
	const modal = useTypedModal();
	const formId = 'addNewPlace';
	const form = useForm<AddNewPlaceForm>({
		resolver: yupResolver(yup.object({
			name: yup.string().required(VALIDATION_MESSAGES.required),
			address: yup.mixed<X4bLocation>().required(VALIDATION_MESSAGES.required),
		})),
		mode: 'all',
	});

	const isValid = form.formState.isValid;

	const savePointMutation = useMutation({
		mutationFn: async (data: AddNewPlaceForm) => {
			const request: SaveCompanyPointRequest = {
				PointType: pointType,
				Latitude: data.address.Latitude,
				Longitude: data.address.Longitude,
				Name: data.name,
				Address: data.address.Address,
				PlaceName: data.address.PlaceName,
			};
			const response = await axiosInstance.post<GenericResponse<SaveCompanyPointResponse>>(
				ENDPOINTS['/api/X4bActivity/SaveCompanyPoint'],
				request,
			);

			if (!response.data.Success) throw new Error(response.data.ErrorMessage || 'Failed to save location. Please try again.');
			return response.data;
		},
		onSuccess: (response) => {
			showNotification('New place saved.', undefined, {
				name: 'Undo',
				handler: async () => {
					await axiosInstance.post<BaseResponse>(
						ENDPOINTS['/api/X4bActivity/DeleteCompanyPoint'],
						{ Id: response.Data.Id },
					);
				}
			});
			modal.resolveRemove();
		},
		onError: (error) => {
			console.error('Failed to save location:', error);
			showError('Failed to save location. Please try again.');
		}
	});

	const handleSave = async () => {
		const isFormValid = await form.trigger();
		if (!isFormValid) return;

		const data = form.getValues();
		savePointMutation.mutate(data);
	};

	return (
		<Dialog
			unstyled
			onHide={() => modal.resolveRemove()}
			visible={true}
			maskStyle={{ backgroundColor: '#00000047' }}
			closeIcon={(<MdClose size={24} className="text-onSurfaceVariant rounded-full duration-200 hover:bg-gray-200" />)}
			pt={{
				root: { className: 'p-9 rounded-[28px] w-[688px] tablet:!w-[calc(100vw-6rem)] tablet:!max-w-[calc(100vw-6rem)] bg-surfaceContainer relative' },
				content: { className: 'p-0' },
				header: { className: 'absolute right-6 top-6' }
			}}
		>
			<Column className="relative gap-6 mb-9">
				<Row className="pl-3 gap-7 items-center">
					<MdArrowBack
						size={24}
						onClick={() => modal.resolveRemove()}
						className="text-onSurfaceVariant cursor-pointer"
					/>
					<TextHeadlineSmall text="Add New Place" className="text-primary" />
				</Row>
				<InputController id="name" control={form.control}>
					<TextInput label="Name" />
				</InputController>
				<AddressInput
					id="address"
					label="Address"
					formName={formId}
					control={form.control}
				/>
			</Column>
			<PickAddressMapModal
				isOpen={isMapOpen}
				handleClose={() => setIsMapOpen(false)}
				initialLatitude={form.getValues('address')?.Latitude}
				initialLongitude={form.getValues('address')?.Longitude}
				onLocationSelected={(location) => {
					if (location.PlaceName) {
						form.setValue('name', location.PlaceName, { shouldValidate: true, shouldDirty: true });
					}
					form.setValue('address', location, { shouldValidate: true, shouldDirty: true });
					setIsMapOpen(false);
				}}
			/>

			<Row className="gap-4 justify-end">
				<Button className="h-10 gap-2 pl-4 pr-6 rounded-[100px] bg-transparent border border-primary"
					type='button'
					onClick={() => {
						setIsMapOpen(true);
					}}
				>
					<MdMap size={18} className="text-primary" />
					<TextLabelLarge2 text="Choose from Map" className="text-primary" />
				</Button>
				<Button
					onClick={handleSave}
					className={cn('h-10 px-6 rounded-[100px]', isValid ? 'bg-primary' : 'bg-[#D2D5D7] pointer-events-none')}
				>
					<TextLabelLarge2 text="Add location" className={isValid ? 'text-white' : 'text-[#8A8C8D]'} />
				</Button>
			</Row>
		</Dialog>
	);
});

export default AddNewPlaceModal;
